import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Trans } from 'react-i18next';
import OnboardingLayout from '../../components/onboarding/OnboardingLayout';
import OptionButton from '../../components/onboarding/OptionButton';
import { useOnboarding } from '../../contexts/OnboardingContext';

const OnboardingStep11 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { responses, updateResponses, markStepCompleted } = useOnboarding();
  const [selected, setSelected] = useState<string[]>(responses.savingsPriorities || []);
  const progress = (11 / 14) * 100;

  const options = [
    { value: 'traveling', label: t('onboarding.step11.options.traveling'), emoji: '🏖️' },
    { value: 'kendaraan_baru', label: t('onboarding.step11.options.kendaraan_baru'), emoji: '🚗' },
    { value: 'rumah_baru', label: t('onboarding.step11.options.rumah_baru'), emoji: '🏠' },
    { value: 'nikah', label: t('onboarding.step11.options.nikah'), emoji: '💍' },
    { value: 'bayi', label: t('onboarding.step11.options.bayi'), emoji: '👶' },
    { value: 'dana_darurat', label: t('onboarding.step11.options.dana_darurat'), emoji: '😌' },
    { value: 'dana_pensiun', label: t('onboarding.step11.options.dana_pensiun'), emoji: '💰' },
    { value: 'investasi', label: t('onboarding.step11.options.investasi'), emoji: '📈' }
  ];

  const toggleOption = (value: string) => {
    if (value === 'none') {
      setSelected([]);
      updateResponses('savingsPriorities', []);
      navigate('/onboarding/step-12');
      return;
    }

    setSelected(prev =>
      prev.includes(value)
        ? prev.filter(v => v !== value)
        : [...prev, value]
    );
  };

  const handleNext = () => {
    updateResponses('savingsPriorities', selected);
    markStepCompleted(11);
    navigate('/onboarding/step-12');
  };

  const handleSkip = () => {
    setSelected([]);
    updateResponses('infrequentExpenses', []);
    navigate('/onboarding/step-12');
  };

  return (
    <OnboardingLayout progress={progress}>
      <div className="bg-white border-4 border-[#077f5d] p-8" style={{ boxShadow: '6px 6px 0 #077f5d' }}>
        <div className="mb-6">
          <h2 className="text-base text-center text-[#077f5d] mb-4 uppercase" style={{ fontFamily: "'Press Start 2P', monospace" }}>
            <Trans i18nKey="onboarding.step11.title" components={{ br: <br /> }} />
          </h2>
        </div>

        <div className="space-y-3 mb-8">
          {options.map(option => (
            <OptionButton
              key={option.value}
              label={option.label}
              emoji={option.emoji}
              selected={selected.includes(option.value)}
              onClick={() => toggleOption(option.value)}
            />
          ))}
        </div>

        {/* Skip option as clickable text */}
        <div className="text-center mb-6">
          <button
            type="button"
            onClick={handleSkip}
            className="text-[#1a1a1a] font-bold opacity-70 hover:opacity-100 hover:underline transition-all duration-150"
            style={{
              fontSize: '14px',
            }}
          >
            {t('onboarding.step11.skip')}
          </button>
        </div>

        <button
          type="button"
          onClick={handleNext}
          disabled={selected.length === 0}
          className="w-full bg-[#3cbe8c] text-white px-8 py-4 border-2 border-[#077f5d] font-bold uppercase   hover:-translate-y-[2px] active:translate-y-[2px] active:shadow-none transition-all duration-75 disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0"
          style={{
            fontFamily: "'Press Start 2P', monospace",
            fontSize: '12px',
            boxShadow: '6px 6px 0 #077f5d'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
            e.currentTarget.style.transform = 'translate(-2px, -2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.boxShadow = '6px 6px 0 #077f5d';
            e.currentTarget.style.transform = 'translate(0, 0)';
          }}
        >
          {t('onboarding.step11.next')}
        </button>
      </div>
    </OnboardingLayout>
  );
};

export default OnboardingStep11;
